import React, { useMemo } from 'react';
import NextImage from 'next/image';
import { useRouter } from 'next/router';
import { toast } from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import { CURRENCY_USD } from 'constants/currency';
import { MY_YOU_PAGE, ROOT_PAGE, getMyTournamentDashboardUrl } from 'constants/pages';
import { getDeleteTournamentEndpoint } from 'constants/payloads/tournaments/delete';
import {
  SubmitMatchesPayload,
  VAIR_SUBMIT_MATCHES_API,
} from 'constants/payloads/vair/submitMatches';
import {
  EventGroupFormatsEnum,
  GetGamedayByEventIdQuery,
  TeamTypesEnum,
  useGetGamedayByEventIdQuery,
  useGetUserEventRegistrationLazyQuery,
  useUpdateEventByIdMutation,
  useUpdateEventGroupByIdMutation,
} from 'types/generated/client';
import { getImageUrl } from 'services/client/cloudflare/getImageUrl';
import { getTimeForMinutes } from 'utils/shared/coachBuilder';
import { getEventExperience } from 'utils/shared/events/getEventExperience';
import { getActivityImageUrlOrDefault } from 'utils/shared/getActivityImageUrl';
import { buildDisplayLocation } from 'utils/shared/location/buildDisplayLocation';
import { calculateEventOrderTotal } from 'utils/shared/money/calculateEventOrderTotal';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { pluralize } from 'utils/shared/pluralize';
import { getSportLabel } from 'utils/shared/sports/getSportLabel';
import toTitleCase from 'utils/shared/string/toTitleCase';
import { convertToUtc } from 'utils/shared/time/convertToUtc';
import { formatSessionCadence } from 'utils/shared/time/formatSessionCadence';
import { getEventSchedule } from 'utils/shared/time/getEventSchedule';
import { getTimezone } from 'utils/shared/time/getTimezone';
import { getTimezoneAbbreviation } from 'utils/shared/time/getTimezoneAbbreviation';
import { getTimezoneOffsetMinutes } from 'utils/shared/time/getTimezoneOffsetMinutes';
import { resolveVenueTimeZone } from 'utils/shared/time/venueDayFilters';
import { useApiGateway } from 'hooks/useApi';
import { useModal } from 'hooks/useModal';
import Calendar from 'svg/Calendar';
import ChevronLeft from 'svg/ChevronLeft';
import Dot from 'svg/Dot';
import Fee from 'svg/Fee';
import Location from 'svg/Location';
import Pencil from 'svg/Pencil';
import Share from 'svg/Share';
import Star from 'svg/Star';
import Trash from 'svg/Trash';
import UserCheck from 'svg/UserCheck';
import {
  DEFAULT_VAIR_SCORE_LIMIT,
  WhenAndWhereData,
} from 'screens/Tournaments/QuickCreatePage/types';
import { getGroupEffectiveTimeRange } from 'screens/TournamentsBuilder/components/CompetitionForm/utils';
import { Button, ButtonLink } from 'components/Button';
import Faqs from 'components/Faqs';
import Link from 'components/Link';
import Pill from 'components/Pill';
import ModalShare from 'components/modals/ModalShare';
import Contacts from './Contacts';
import Events from './Events';
import ModalDeleteEvent from './ModalDeleteEvent';
import ModalEditVairEvent from './ModalEditVairEvent';
import ModalRemainingRounds from './ModalRemainingRounds';
import ModalScoreOverview from './ModalScoreOverview';
import Players from './Players';
import Canceled from './Register/Canceled';
import ModalVairInfo from './Register/ModalVairInfo';
import Sponsored from './Sponsored';
import VairBanner from './VairBanner';
import Venue from './Venue';
import { Props, RemainingRound, RoundStatusData, RoundStatusResult, VairRound } from './types';
import {
  DEFAULT_TIMEZONE,
  areAllMatchesSubmittedToVair,
  formatDate,
  formatDateRange,
  getGroupFormatName,
  isShowGroupFormat,
} from './utils';

const IS_REVIEWS_ENABLED = false;
const IS_SPONSORS_ENABLED = true;
const IS_CONTACT_ENABLED = false;

const DetailCard = ({
  Icon,
  title,
  description,
}: {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
}) => {
  return (
    <div className="flex w-full flex-col items-center justify-center rounded-xl border border-color-border-input-lightmode p-ds-md text-center dark:border-color-border-input-darkmode">
      <Icon className="h-5 w-5 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
      <p className="typography-product-body-highlight mt-ds-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
        {title}
      </p>
      <p className="typography-product-subheading mt-ds-md text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
        {description}
      </p>
    </div>
  );
};
export default function TournamentContent({
  event,
  faqs,
  isPreview,
  isReview = false,
  isEdit,
  eventGroups,
  isUserOrganizer,
}: Props) {
  const router = useRouter();
  const {
    openModal: openShareModal,
    closeModal: closeShareModal,
    isOpen: isShareModalOpen,
  } = useModal();
  const {
    openModal: openScoreOverviewModal,
    closeModal: closeScoreOverviewModal,
    isOpen: isScoreOverviewModalOpen,
  } = useModal();
  const {
    openModal: openVairInfoModal,
    closeModal: closeVairInfoModal,
    isOpen: isVairInfoModalOpen,
  } = useModal();
  const {
    openModal: openEditVairModal,
    closeModal: closeEditVairModal,
    isOpen: isEditVairModalOpen,
  } = useModal();
  const {
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
    isOpen: isDeleteModalOpen,
  } = useModal();
  const hasOrganizerImage = !!event.organizerImageUrl && !!event.organizerImagePath;
  const hasProfileImage =
    !!event.hostUserProfile?.profileImagePath && !!event.hostUserProfile?.profileImageProviderUrl;
  // Venue first: `timezoneName` is captured from the organizer's browser at creation
  // time, so an organizer creating a New York event from abroad stamps their own zone
  // on it. The venue's zone is where the event physically happens.
  const timeZone = resolveVenueTimeZone(event) || DEFAULT_TIMEZONE;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const userTimeZone = getTimezone();

  // Mutations for updating VAIR events
  const [updateEventMutation] = useUpdateEventByIdMutation();
  const [updateEventGroupMutation] = useUpdateEventGroupByIdMutation();

  const isVairRoundRobin = useMemo(
    () => event.groups?.some((group) => group.format === EventGroupFormatsEnum.VairRoundRobin),
    [event.groups],
  );

  const isVairEvent = isVairRoundRobin || !!event.isVairRequired;
  const experience = getEventExperience(event);
  const sessionCadence = formatSessionCadence(getEventSchedule(event).sessions, timeZone);
  const eventNoun = experience.noun;

  const shareAction = () => {
    openShareModal();
    if (navigator.share) {
      navigator
        .share({
          title: `${experience.label || 'Event'} on Bounce: ${event.title}`,
          text: `${experience.joinVerb} the ${event.title} ${eventNoun}, in ${
            event.city?.name && event.city?.countrySubdivision?.code
              ? `${event.city?.name}, ${event.city?.countrySubdivision?.code}`
              : event.displayLocation
          }! More details at ${shareUrl}`,
          url: shareUrl,
        })
        .then(() => console.log('Successful share'))
        .catch((error) => console.log('Error sharing:', error));
    }
  };

  const getBannerImage = () => {
    if (isReview && event.coverImageUrl) return event.coverImageUrl;

    if (event.coverImageUrl && event.coverImagePath) {
      return getImageUrl({ url: event.coverImageUrl, path: event.coverImagePath });
    }
    if (isVairEvent) return '/images/vair/vair-rr.png';

    return getActivityImageUrlOrDefault(event);
  };

  const [
    getUserEventRegistrationLazyQuery,
    { data, loading: isRegistrationLoading, called: isRegistrationCalled },
  ] = useGetUserEventRegistrationLazyQuery();

  const {
    data: gamedayData,
    loading: isGamedayLoading,
    refetch: refetchGameday,
  } = useGetGamedayByEventIdQuery({
    variables: { id: event.id },
    skip: !isVairEvent || !isUserOrganizer,
    fetchPolicy: 'cache-first',
  });

  // State to store round data for modals
  const [roundStatusData, setRoundStatusData] = React.useState<RoundStatusData>({
    remainingRounds: [],
    completedRounds: [],
    totalMatches: 0,
    totalRounds: 0,
    totalIncompleteMatches: 0,
  });

  // Check if all matches are submitted to VAIR
  const areAllMatchesSubmitted = React.useMemo(
    () => areAllMatchesSubmittedToVair(gamedayData, isVairEvent),
    [gamedayData, isVairEvent],
  );

  // API gateway for submitting matches to VAIR
  const {
    post: submitMatchesToVair,
    isLoading: isSubmittingScores,
    isError: isSubmissionError,
    error: submissionError,
  } = useApiGateway<SubmitMatchesPayload>(VAIR_SUBMIT_MATCHES_API);

  // API gateway for deleting event
  const { delete: deleteEventRequest, isLoading: isDeletingEvent } = useApiGateway<void>(
    getDeleteTournamentEndpoint(event.id),
  );

  const existingRegistrations = data;
  const hasPreviousEventRegistration = !!existingRegistrations?.eventRegistrations?.length;
  const hasPreviousGroupRegistration = !!existingRegistrations?.eventGroupRegistrations?.length;
  const orderTotal = calculateEventOrderTotal({
    event,
    registration: event.groups as any,
    isFirstTournamentRegistration:
      !existingRegistrations?.eventRegistrations ||
      !existingRegistrations?.eventRegistrations.length,
  });

  // A pack is priced on its single group; its fee reads "$80 · 4 sessions".
  const packGroup = experience.isPack && event.groups.length === 1 ? event.groups[0] : null;
  const registrationFeeLabel = packGroup
    ? `${
        convertUnitPriceToFormattedPrice(
          orderTotal.subtotal + (packGroup.priceUnitAmount || 0),
          CURRENCY_USD,
        ).priceDisplay
      } · ${pluralize({ count: packGroup.timeSlots?.length ?? 0, singular: 'session' })}`
    : convertUnitPriceToFormattedPrice(orderTotal.subtotal, CURRENCY_USD).priceDisplay;

  const registrationCount = event.isExternal
    ? event.sourceRegistrationCount || 0
    : event?.registrationsAggregate?.aggregate?.count || 0;

  // Determine round completion status from gameday data
  const determineRoundStatus = (
    data: GetGamedayByEventIdQuery | null | undefined,
  ): RoundStatusResult => {
    if (!isVairEvent || !data?.eventsByPk?.groups) {
      return {
        isComplete: true,
        remainingRounds: [],
        completedRounds: [],
        totalMatches: 0,
        totalRounds: 0,
        totalIncompleteMatches: 0,
      };
    }

    const remainingRounds: RemainingRound[] = [];
    const completedRounds: VairRound[] = [];

    for (const group of data.eventsByPk.groups) {
      if (group.format !== EventGroupFormatsEnum.VairRoundRobin) continue;

      for (const sequence of group.sequences || []) {
        for (const pool of sequence.pools || []) {
          for (const round of pool.rounds || []) {
            let incompleteMatches = 0;

            for (const match of round.matches || []) {
              // Check if match has scores (is complete)
              const hasScores = match.games?.some(
                (game: { team1Score?: number | null; team2Score?: number | null }) =>
                  typeof game.team1Score === 'number' && typeof game.team2Score === 'number',
              );

              if (!hasScores) {
                incompleteMatches++;
              }
            }

            if (incompleteMatches > 0) {
              remainingRounds.push({
                roundOrder: round.roundOrder,
                title: round.title,
                incompleteMatches,
              });
            } else {
              completedRounds.push(round);
            }
          }
        }
      }
    }

    const totalMatches = completedRounds.reduce(
      (sum, round) => sum + (round.matches?.length || 0),
      0,
    );
    const totalIncompleteMatches = remainingRounds.reduce(
      (sum, round) => sum + round.incompleteMatches,
      0,
    );

    return {
      isComplete: remainingRounds.length === 0,
      remainingRounds,
      completedRounds,
      totalMatches,
      totalRounds: completedRounds.length + remainingRounds.length,
      totalIncompleteMatches,
    };
  };

  const handleUploadScoreClick = async () => {
    // Refetch gameday data to get fresh state
    const result = await refetchGameday();

    // Check if gameday has started (has rounds/matches)
    const hasRoundsOrMatches = result.data?.eventsByPk?.groups?.some((group) => {
      if (group.format !== EventGroupFormatsEnum.VairRoundRobin) return false;
      return group.sequences?.some((sequence) =>
        sequence.pools?.some((pool) => pool.rounds && pool.rounds.length > 0),
      );
    });

    // If Gameday hasn't started, show the same modal as Gameday button
    if (!hasRoundsOrMatches) {
      openVairInfoModal();
      return;
    }

    // Determine the case from the result
    const roundStatus = determineRoundStatus(result.data);

    // Set state for modal data
    setRoundStatusData(roundStatus);

    // Always show score overview modal
    openScoreOverviewModal();
  };

  const handleSubmitScores = async () => {
    try {
      // Get group and event information
      const firstRound = roundStatusData.completedRounds[0];
      if (!firstRound) {
        throw new Error('No completed rounds to submit');
      }

      const gamedayResult = await refetchGameday();

      const group = gamedayResult.data?.eventsByPk?.groups?.find(
        (g) => g.format === EventGroupFormatsEnum.VairRoundRobin,
      );

      if (!group) {
        throw new Error('VAIR group not found');
      }

      // VAIR needs the group's scoring rules; abort rather than submit defaults.
      if (group.totalPoints == null || group.winBy == null) {
        throw new Error('This group is missing its scoring configuration (points / win by).');
      }

      // Prepare location
      const location =
        event.venue?.title ||
        event.displayLocation ||
        event.city?.name ||
        event.venue?.addressString ||
        '';

      // Prepare match date (prefer timeSlots, fallback to group.startsAt/endsAt)
      const { startsAt: effectiveStartsAt } = getGroupEffectiveTimeRange(group);
      const matchDate =
        effectiveStartsAt || event.startDateTime
          ? new Date(effectiveStartsAt || event.startDateTime).toISOString()
          : new Date().toISOString();

      const response = await submitMatchesToVair({
        payload: {
          eventId: event.id,
          completedRounds: roundStatusData.completedRounds,
          groupTitle: group.title,
          eventTitle: event.title,
          location,
          matchDate,
          // Tournament groups always carry a team type; null is only for open play games.
          teamType: group.teamType ?? TeamTypesEnum.Doubles,
          winScore: group.totalPoints,
          winBy: group.winBy,
        },
      });

      if (response.isError || isSubmissionError) {
        throw new Error(submissionError || 'Failed to submit matches to VAIR');
      }

      toast.success('Successfully submitted matches to VAIR');

      // Refetch gameday data to update submission status
      await refetchGameday();

      closeScoreOverviewModal();
    } catch (error) {
      console.error('Error submitting scores to VAIR:', error);

      toast.error(
        error instanceof Error ? error.message : 'Failed to submit scores. Please try again.',
      );
    }
  };

  const handleUpdateVairEvent = async (data: WhenAndWhereData) => {
    try {
      const vairGroup = event.groups?.find(
        (g) => g.format === EventGroupFormatsEnum.VairRoundRobin,
      );

      if (!vairGroup) {
        throw new Error('VAIR group not found');
      }

      const startDateTime = convertToUtc({
        time: getTimeForMinutes(data.selectStartTime),
        date: data.selectDate,
        timezone: userTimeZone,
      });

      await updateEventMutation({
        variables: {
          id: event.id,
          _set: {
            title: data.title,
            startDateTime,
            endDateTime: startDateTime, // Same day event
            venueId: data.venue?.id || null,
            displayLocation: buildDisplayLocation(
              data.venue?.city,
              data.venue?.address || event.addressString,
            ),
            cityId: data.venue?.city?.id || event.cityId,
            addressString: data.venue?.address,
            latitude: data.venue?.latitude,
            longitude: data.venue?.longitude,
            geometry:
              data.venue?.latitude && data.venue?.longitude
                ? {
                    type: 'Point',
                    coordinates: [data.venue.longitude, data.venue.latitude],
                  }
                : event.geometry,
            timezoneName: userTimeZone,
            timezoneAbbreviation: getTimezoneAbbreviation(userTimeZone),
            timezoneOffsetMinutes: getTimezoneOffsetMinutes(userTimeZone),
          },
        },
      });

      // Update VAIR group and refetch once after both complete
      await updateEventGroupMutation({
        variables: {
          id: vairGroup.id,
          _set: {
            title: data.title,
            totalPoints: data.scoreLimit ?? DEFAULT_VAIR_SCORE_LIMIT,
            startsAt: startDateTime,
            endsAt: startDateTime,
          },
        },
        refetchQueries: ['getEventDetails', 'GetGamedayByEventId'],
        awaitRefetchQueries: true,
      });

      toast.success('Event updated successfully!');
      closeEditVairModal();
    } catch (error) {
      console.error('Error updating VAIR event:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to update event. Please try again.',
      );
    }
  };

  const handleDeleteEvent = async () => {
    try {
      const response = await deleteEventRequest();

      if (response.isError) {
        throw new Error('Failed to delete tournament');
      }

      toast.success('Tournament deleted successfully');
      closeDeleteModal();

      // Redirect to the home page
      router.push(ROOT_PAGE);
    } catch (error) {
      console.error('Error deleting tournament:', error);

      toast.error(
        error instanceof Error ? error.message : 'Failed to delete tournament. Please try again.',
      );
    }
  };

  const eventLocation = [event.city?.name, event.city?.countrySubdivision?.name]
    .filter(Boolean)
    .join(', ');
  const eventSeoDescription = `Join this ${getSportLabel(event.sport).toLowerCase()} event${
    eventLocation ? ` in ${eventLocation}` : ''
  }. Find event information, match formats, and ways to join local play.`;

  return (
    <>
      <div className="">
        <div className="mb-ds-lg hidden items-center justify-between gap-ds-lg lg:flex">
          <div className="flex items-center gap-4">
            <p className="typography-product-subheading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              Organizer
            </p>
            <div className="flex flex-row items-center">
              {(hasOrganizerImage || hasProfileImage) && (
                <>
                  <NextImage
                    src={
                      event.organizerImageUrl && event.organizerImagePath
                        ? getImageUrl({
                            url: event.organizerImageUrl,
                            path: event.organizerImagePath,
                          })
                        : event.hostUserProfile?.profileImagePath &&
                          event.hostUserProfile?.profileImageProviderUrl
                        ? getImageUrl({
                            url: event.hostUserProfile.profileImageProviderUrl,
                            path: event.hostUserProfile.profileImagePath,
                          })
                        : ''
                    }
                    alt={event.sourceOrganizerTitle || 'Organizer'}
                    className="h-8 w-8 rounded-full"
                    height={24}
                    width={38}
                  />
                </>
              )}
              {(!event.organizerImageUrl &&
                !event.organizerImagePath &&
                !event.hostUserProfile?.profileImagePath &&
                !event.hostUserProfile?.profileImageProviderUrl) ||
                ((!!event?.sourceOrganizerTitle || !!event?.hostUserProfile?.fullName) && (
                  <p className="typography-product-caption truncate pl-2 pr-1 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                    {toTitleCase(
                      event?.sourceOrganizerTitle ?? event?.hostUserProfile?.fullName ?? '',
                    )}
                  </p>
                ))}
            </div>
          </div>

          {!event.isExternal && IS_REVIEWS_ENABLED && (
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 text-color-text-lightmode-secondary" />
              <div className="flex items-center gap-1">
                <p className="typography-product-element-label text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"></p>
                <Dot className="dark: h-1 w-1 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
                <p className="typography-product-element-label text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  reviews
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-4">
            {isUserOrganizer ? (
              <>
                {!isReview && (
                  <>
                    {isVairRoundRobin ? (
                      <>
                        <Button
                          variant="inverted"
                          isInline
                          size="sm"
                          className="flex items-center text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
                          onClick={() => openEditVairModal()}
                          iconLeft={
                            <Pencil className="mr-1 h-4 w-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
                          }
                        >
                          Edit
                        </Button>
                        <Button
                          variant="inverted"
                          isInline
                          size="sm"
                          className="text-color-error-500 dark:text-color-error-400 flex items-center"
                          onClick={() => openDeleteModal()}
                          loading={isDeletingEvent}
                          iconLeft={
                            <Trash className="text-color-error-500 dark:text-color-error-400 mr-1 h-4 w-4" />
                          }
                        >
                          Delete
                        </Button>
                      </>
                    ) : (
                      <ButtonLink
                        variant="inverted"
                        isInline
                        size="sm"
                        className="flex items-center text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
                        href={`${getMyTournamentDashboardUrl(event.id)}?tab=Settings`}
                        iconLeft={
                          <Pencil className="mr-1 h-4 w-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
                        }
                      >
                        Edit {eventNoun}
                      </ButtonLink>
                    )}
                  </>
                )}
                {!isPreview && (
                  <>
                    <Button
                      variant="primary"
                      isInline
                      size="sm"
                      onClick={shareAction}
                      iconLeft={
                        <Share className="mr-1 h-4 w-4 text-color-text-lightmode-invert dark:text-color-text-darkmode-invert" />
                      }
                    >
                      Share{isVairEvent ? '' : ` ${eventNoun}`}
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                {!isPreview && !isEdit && (
                  <>
                    <Button
                      variant="primary-light"
                      isInline
                      size="sm"
                      className="flex items-center text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
                      onClick={shareAction}
                      iconLeft={
                        <Share className="mr-1 h-4 w-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
                      }
                    >
                      Share
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <div className="relative flex flex-row items-center justify-between">
          <div className="flex flex-row justify-between">
            {!isReview && (
              <>
                <div className="absolute left-4 top-4 block lg:hidden">
                  <Link
                    className="flex cursor-pointer items-center justify-center rounded-3xl bg-color-bg-lightmode-primary p-2 shadow-lg dark:bg-color-bg-darkmode-primary"
                    href={MY_YOU_PAGE}
                  >
                    <ChevronLeft className="w-6" />
                  </Link>
                </div>
                {isUserOrganizer && (
                  <div className="absolute right-4 top-4 flex gap-2 lg:hidden">
                    <ButtonLink
                      className="border-none text-base shadow-lg"
                      variant="secondary"
                      isInline
                      size="sm"
                      iconLeft={<Pencil className="w-4" />}
                      href={`${getMyTournamentDashboardUrl(event.id)}?tab=Settings`}
                    >
                      Edit
                    </ButtonLink>
                    {isVairEvent && (
                      <Button
                        className="text-color-error-500 dark:text-color-error-400 border-none text-base shadow-lg"
                        variant="secondary"
                        isInline
                        size="sm"
                        loading={isDeletingEvent}
                        iconLeft={
                          <Trash className="text-color-error-500 dark:text-color-error-400 w-4" />
                        }
                        onClick={() => openDeleteModal()}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <NextImage
            src={getBannerImage()}
            alt={event.title}
            className="h-[12.5rem] w-full object-cover object-center lg:h-[20.625rem] lg:rounded-xl"
            height={330}
            width={700}
          />
        </div>
        {(isVairRoundRobin || event.isVairRequired) && <VairBanner />}
        <div className="px-4 lg:p-0">
          <div className="mt-4 flex items-center gap-4 lg:hidden">
            <p className="typography-product-subheading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              Organizer
            </p>
            <div className="flex flex-row items-center">
              {(hasOrganizerImage || hasProfileImage) && (
                <img
                  src={
                    event.organizerImageUrl && event.organizerImagePath
                      ? getImageUrl({
                          url: event.organizerImageUrl,
                          path: event.organizerImagePath,
                        })
                      : event.hostUserProfile?.profileImagePath &&
                        event.hostUserProfile?.profileImageProviderUrl
                      ? getImageUrl({
                          url: event.hostUserProfile.profileImageProviderUrl,
                          path: event.hostUserProfile.profileImagePath,
                        })
                      : ''
                  }
                  alt="Organizer"
                  className="h-8 w-8 rounded-full"
                />
              )}
              {(!event.organizerImageUrl &&
                !event.organizerImagePath &&
                !event.hostUserProfile?.profileImagePath &&
                !event.hostUserProfile?.profileImageProviderUrl) ||
                ((!!event?.sourceOrganizerTitle || !!event?.hostUserProfile?.fullName) && (
                  <p className="typography-product-caption truncate pl-2 pr-1 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                    {toTitleCase(
                      event?.sourceOrganizerTitle ?? event?.hostUserProfile?.fullName ?? '',
                    )}
                  </p>
                ))}
            </div>
            {!event.isExternal && IS_REVIEWS_ENABLED && (
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-color-text-lightmode-secondary" />
                <div className="flex items-center gap-1">
                  <p className="typography-product-element-label text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"></p>
                  <Dot className="dark: h-1 w-1 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
                  <p className="typography-product-element-label text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    reviews
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="mt-4 flex items-center gap-4 lg:mt-ds-xl">
            <h1 className="typography-product-heading w-full break-all text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              {event.title}
            </h1>
            {isUserOrganizer &&
              isVairEvent &&
              (areAllMatchesSubmitted ? (
                <Pill type="success" text="Score Submitted" className="whitespace-nowrap" />
              ) : (
                <Button
                  variant="secondary"
                  sizeDesktop="lg"
                  sizeMobile="md"
                  isInline
                  className="self-start whitespace-nowrap"
                  loading={isGamedayLoading}
                  onClick={handleUploadScoreClick}
                >
                  Upload Score
                </Button>
              ))}
          </div>
          {(event?.venue?.addressString || event?.addressString) && (
            <h3 className="typography-product-caption mt-3 flex flex-row gap-2 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              <Location className="h-4 w-4 text-color-bg-lightmode-icon dark:text-color-bg-darkmode-icon" />
              {event.venue?.addressString || event.addressString}
            </h3>
          )}
          {!event.isExternal &&
            experience.hasCompetition &&
            !!event.groupFormat &&
            isShowGroupFormat(event.groupFormat) && (
              <p className="typography-product-subheading mt-ds-md inline-flex rounded-full bg-color-bg-lightmode-brand-secondary px-ds-lg py-ds-sm text-color-brand-primary dark:bg-color-bg-lightmode-brand-secondary lg:mt-ds-lg">
                {getGroupFormatName(event.groupFormat)}
              </p>
            )}
          <div className="fixed -left-40 -top-40 -z-100">
            <p>{eventSeoDescription}</p>
          </div>
          {!!event.canceledAt && (
            <div className="mt-ds-2xl overflow-hidden rounded-3xl border border-color-border-input-lightmode dark:border-color-border-input-darkmode lg:hidden">
              <Canceled event={event} />
            </div>
          )}
          <div className="mt-ds-2xl grid grid-cols-1 gap-ds-xl lg:grid-cols-3">
            <DetailCard
              Icon={Calendar}
              title={experience.dateCardTitle}
              description={[
                sessionCadence,
                formatDateRange({
                  startDate: event.startDateTime,
                  endDate: event.endDateTime,
                  timeZone: timeZone,
                }),
              ]
                .filter(Boolean)
                .join(' · ')}
            />
            <DetailCard
              Icon={UserCheck}
              title="Registration Deadline"
              description={formatDate({
                date:
                  event.registrationDeadlineDateTime ||
                  event.registrationDeadlineDate ||
                  event.startDateTime,
                timeZone: timeZone,
              })}
            />
            <DetailCard Icon={Fee} title="Registration Fee" description={registrationFeeLabel} />
            <div className="right-4 top-4 block lg:hidden">
              <Button
                variant="primary"
                size="lg"
                className="flex w-full items-center"
                onClick={shareAction}
                iconLeft={<Share className="mr-1 h-4 w-4 " />}
              >
                Share my {eventNoun}
              </Button>
            </div>
          </div>
          {!!event.description && (
            <div className="prose mt-ds-2xl w-full break-normal ![overflow-wrap:anywhere] dark:prose-invert">
              <ReactMarkdown
                components={{
                  p: ({ node, ...props }) => (
                    <p
                      className="text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
                      {...props}
                    />
                  ),
                  li: ({ node, ...props }) => (
                    <li
                      className="text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
                      {...props}
                    />
                  ),
                  a: ({ node, ...props }) => (
                    <a
                      className="text-color-text-lightmode-secondary underline dark:text-color-text-darkmode-secondary"
                      {...props}
                      target="_blank"
                      rel="noreferrer noopener nofollow"
                    />
                  ),
                }}
              >
                {event.description}
              </ReactMarkdown>
            </div>
          )}
          {event.isShowPlayersGoing && registrationCount > 0 && !event.canceledAt && (
            <div className="mt-ds-3xl border-y border-color-border-input-lightmode py-ds-2xl dark:border-color-border-input-darkmode">
              <Players event={event} />
            </div>
          )}
          {!event.isExternal &&
            ((!!eventGroups?.length && isReview) || (!!event?.groups && !isReview)) && (
              <div className="border-b border-color-border-input-lightmode py-ds-2xl dark:border-color-border-input-darkmode">
                <Events event={event} eventGroups={eventGroups} isReview={isReview} />
              </div>
            )}
          {event.venue && (event.venue.id || event.venue.addressString) && (
            <div className="py-4">
              <Venue event={event} />
            </div>
          )}
          {IS_SPONSORS_ENABLED && !!event.sponsors?.length && (
            <div className="border-t border-color-border-input-lightmode py-ds-2xl dark:border-color-border-input-darkmode">
              <Sponsored sponsors={event.sponsors} />
            </div>
          )}
          {faqs && faqs.length > 0 && (
            <div className="border-t border-color-border-input-lightmode py-ds-2xl dark:border-color-border-input-darkmode">
              <Faqs questionsAnswers={faqs} />
            </div>
          )}
          {IS_CONTACT_ENABLED && <Contacts />}
        </div>
      </div>
      <ModalVairInfo
        isOpen={isVairInfoModalOpen}
        onClose={closeVairInfoModal}
        title="Gameday Not Started"
        message="You can only upload score once Gameday has started."
      />
      <ModalShare
        isOpen={isShareModalOpen}
        closeModal={closeShareModal}
        shareUrl={shareUrl}
        title={`Share ${eventNoun}`}
      />
      <ModalScoreOverview
        isOpen={isScoreOverviewModalOpen}
        closeModal={closeScoreOverviewModal}
        onSubmit={handleSubmitScores}
        rounds={roundStatusData.completedRounds}
        remainingRounds={roundStatusData.remainingRounds}
        totalMatches={roundStatusData.totalMatches}
        totalRounds={roundStatusData.totalRounds}
        totalIncompleteMatches={roundStatusData.totalIncompleteMatches}
        eventName={event.title}
        isLoading={isSubmittingScores}
      />
      <ModalEditVairEvent
        isOpen={isEditVairModalOpen}
        onClose={closeEditVairModal}
        event={event}
        onUpdate={handleUpdateVairEvent}
      />
      <ModalDeleteEvent
        isOpen={isDeleteModalOpen}
        closeModal={() => closeDeleteModal()}
        onDelete={handleDeleteEvent}
        eventTitle={event.title}
        isLoading={isDeletingEvent}
      />
    </>
  );
}
